/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ChangeCurrentPasswordDto } from '../models/ChangeCurrentPasswordDto';
import type { ChangeForgottenPasswordDto } from '../models/ChangeForgottenPasswordDto';
import type { ForgotPasswordDto } from '../models/ForgotPasswordDto';
import type { SendInvitationLinkDto } from '../models/SendInvitationLinkDto';
import type { SendRegistrationEmailDto } from '../models/SendRegistrationEmailDto';
import type { SiteEmployeeDto } from '../models/SiteEmployeeDto';
import type { SiteEmployeeExistsDto } from '../models/SiteEmployeeExistsDto';
import type { SiteEmployeeInfoDto } from '../models/SiteEmployeeInfoDto';
import type { SiteEmployeeInvitedCheckDto } from '../models/SiteEmployeeInvitedCheckDto';
import type { SiteEmployeeRegisterDto } from '../models/SiteEmployeeRegisterDto';
import type { SiteEmployeeSelfUpdateDto } from '../models/SiteEmployeeSelfUpdateDto';
import type { SiteEmployeeUpdateDto } from '../models/SiteEmployeeUpdateDto';
import type { StudyDto } from '../models/StudyDto';
import type { UpdateNotificationDto } from '../models/UpdateNotificationDto';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class SiteEmployeeService {

    /**
     * Get site employees
     * @param siteid
     * @returns SiteEmployeeDto
     * @throws ApiError
     */
    public static siteEmployeeControllerList(
        siteid?: string,
    ): CancelablePromise<Array<SiteEmployeeDto>> {
        return __request({
            method: 'GET',
            path: `/siteemployee`,
            query: {
                'siteid': siteid,
            },
        });
    }

    /**
     * Create site employee
     * @param requestBody
     * @returns SiteEmployeeDto
     * @throws ApiError
     */
    public static siteEmployeeControllerCreate(
        requestBody: SiteEmployeeUpdateDto,
    ): CancelablePromise<SiteEmployeeDto> {
        return __request({
            method: 'POST',
            path: `/siteemployee`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Get site employees basic data
     * @param siteid
     * @returns SiteEmployeeInfoDto
     * @throws ApiError
     */
    public static siteEmployeeControllerListInfo(
        siteid?: string,
    ): CancelablePromise<Array<SiteEmployeeInfoDto>> {
        return __request({
            method: 'GET',
            path: `/siteemployee/info`,
            query: {
                'siteid': siteid,
            },
        });
    }

    /**
     * Get current site employee
     * @returns SiteEmployeeDto
     * @throws ApiError
     */
    public static siteEmployeeControllerGetCurrentSiteEmployee(): CancelablePromise<SiteEmployeeDto> {
        return __request({
            method: 'GET',
            path: `/siteemployee/current`,
        });
    }

    /**
     * Update current site employee
     * @param requestBody
     * @returns SiteEmployeeDto
     * @throws ApiError
     */
    public static siteEmployeeControllerUpdateCurrentSiteEmployee(
        requestBody: SiteEmployeeSelfUpdateDto,
    ): CancelablePromise<SiteEmployeeDto> {
        return __request({
            method: 'PUT',
            path: `/siteemployee/current`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Get current site employee
     * @param siteId
     * @returns SiteEmployeeDto
     * @throws ApiError
     */
    public static siteEmployeeControllerGetBySiteId(
        siteId: string,
    ): CancelablePromise<Array<SiteEmployeeDto>> {
        return __request({
            method: 'GET',
            path: `/siteemployee/by-site-id/${siteId}`,
        });
    }

    /**
     * Update current user login info
     * @returns SiteEmployeeDto
     * @throws ApiError
     */
    public static siteEmployeeControllerUpdateCurrentUserLoginInfo(): CancelablePromise<SiteEmployeeDto> {
        return __request({
            method: 'PUT',
            path: `/siteemployee/currentUserLoginInfo`,
        });
    }

    /**
     * Update current site employee password
     * @param requestBody
     * @returns void
     * @throws ApiError
     */
    public static siteEmployeeControllerUpdateCurrentPassword(
        requestBody: ChangeCurrentPasswordDto,
    ): CancelablePromise<void> {
        return __request({
            method: 'PUT',
            path: `/siteemployee/current/updatePassword`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Sends password reset email to site employee email
     * @param requestBody
     * @returns void
     * @throws ApiError
     */
    public static siteEmployeeControllerSendForgotPasswordEmail(
        requestBody: ForgotPasswordDto,
    ): CancelablePromise<void> {
        return __request({
            method: 'POST',
            path: `/siteemployee/forgotPassword`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Changes the user password if the input token is correct
     * @param requestBody
     * @returns void
     * @throws ApiError
     */
    public static siteEmployeeControllerChangeForgottenPassword(
        requestBody: ChangeForgottenPasswordDto,
    ): CancelablePromise<void> {
        return __request({
            method: 'POST',
            path: `/siteemployee/changeForgottenPassword`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Sends invitation link email to site employee in order to activate an account
     * @param requestBody
     * @returns void
     * @throws ApiError
     */
    public static siteEmployeeControllerSendInvitationLinkEmail(
        requestBody: SendInvitationLinkDto,
    ): CancelablePromise<void> {
        return __request({
            method: 'POST',
            path: `/siteemployee/send-invitation-link`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Check if site employee exists in dev database, workaround for database restore issue (auth0 users not cleaned up)
     * @param email
     * @returns SiteEmployeeExistsDto
     * @throws ApiError
     */
    public static siteEmployeeControllerResetPasswordCheck(
        email: string,
    ): CancelablePromise<SiteEmployeeExistsDto> {
        return __request({
            method: 'GET',
            path: `/siteemployee/resetPasswordCheck`,
            query: {
                'email': email,
            },
        });
    }

    /**
     * Get site employee
     * @param id
     * @returns SiteEmployeeDto
     * @throws ApiError
     */
    public static siteEmployeeControllerGet(
        id: string,
    ): CancelablePromise<SiteEmployeeDto> {
        return __request({
            method: 'GET',
            path: `/siteemployee/${id}`,
        });
    }

    /**
     * Update site employee
     * @param id
     * @param requestBody
     * @returns SiteEmployeeDto
     * @throws ApiError
     */
    public static siteEmployeeControllerUpdate(
        id: string,
        requestBody: SiteEmployeeUpdateDto,
    ): CancelablePromise<SiteEmployeeDto> {
        return __request({
            method: 'PUT',
            path: `/siteemployee/${id}`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Delete site employee
     * @param id
     * @returns void
     * @throws ApiError
     */
    public static siteEmployeeControllerDelete(
        id: string,
    ): CancelablePromise<void> {
        return __request({
            method: 'DELETE',
            path: `/siteemployee/${id}`,
            errors: {
                412: `Error that should be passed to user.`,
            },
        });
    }

    /**
     * Get site employee by email
     * @param email
     * @returns SiteEmployeeDto
     * @throws ApiError
     */
    public static siteEmployeeControllerGetByEmail(
        email: string,
    ): CancelablePromise<SiteEmployeeDto> {
        return __request({
            method: 'GET',
            path: `/siteemployee/getByEmail/${email}`,
        });
    }

    /**
     * Confirm site employee registration and add them to Auth0
     * @param requestBody
     * @returns SiteEmployeeDto
     * @throws ApiError
     */
    public static siteEmployeeControllerFinishRegistration(
        requestBody: SiteEmployeeRegisterDto,
    ): CancelablePromise<SiteEmployeeDto> {
        return __request({
            method: 'POST',
            path: `/siteemployee/finishRegistration`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Finalize site employee registration via external OIDC (ID.ME or Auth0 SSO at login)
     * @param token
     * @returns SiteEmployeeDto
     * @throws ApiError
     */
    public static siteEmployeeControllerAcceptRegistrationViaExternalOidc(
        token: string,
    ): CancelablePromise<SiteEmployeeDto> {
        return __request({
            method: 'POST',
            path: `/siteemployee/registration/${token}/external/accept`,
        });
    }

    /**
     * Send registration email to site employee
     * @param requestBody
     * @returns void
     * @throws ApiError
     */
    public static siteEmployeeControllerSendRegistrationEmail(
        requestBody: SendRegistrationEmailDto,
    ): CancelablePromise<void> {
        return __request({
            method: 'POST',
            path: `/siteemployee/sendRegistrationEmail`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Check if the registration email is already registered
     * @param requestBody
     * @returns void
     * @throws ApiError
     */
    public static siteEmployeeControllerCanRegisterEmail(
        requestBody: SiteEmployeeInvitedCheckDto,
    ): CancelablePromise<void> {
        return __request({
            method: 'POST',
            path: `/siteemployee/canRegisterEmail`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Remove site employee from site
     * @param siteEmployeeId
     * @param siteId
     * @returns StudyDto
     * @throws ApiError
     */
    public static siteEmployeeControllerRemoveFromSite(
        siteEmployeeId: string,
        siteId: string,
    ): CancelablePromise<Array<StudyDto>> {
        return __request({
            method: 'PUT',
            path: `/siteemployee/${siteEmployeeId}/removeFromSite/${siteId}`,
        });
    }

    /**
     * Update site employee show notification value
     * @param siteEmployeeId
     * @param requestBody
     * @returns SiteEmployeeDto
     * @throws ApiError
     */
    public static siteEmployeeControllerUpdateNotification(
        siteEmployeeId: string,
        requestBody: UpdateNotificationDto,
    ): CancelablePromise<SiteEmployeeDto> {
        return __request({
            method: 'PUT',
            path: `/siteemployee/${siteEmployeeId}/updateNotification`,
            body: requestBody,
            mediaType: 'application/json',
        });
    }

}